/**
 * /reset-password?token=...: choose a new password with the emailed link.
 *
 * The token is read once, then removed from the address bar (and from the
 * browser history) so it does not linger in the URL. The server consumes it
 * and signs the user out everywhere; the page then sends them to sign in.
 *
 * B3-reset-password (W6): a missing or dead link says why (cut by the mail
 * client, expired, already used) and what to do; the new password has a
 * strength meter and « Afficher », the button stays active and the rules are
 * checked at submit.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { resetPassword } from '@/api/endpoints/password';
import { ApiErrorException } from '@/api/client';
import { newPasswordError, passwordApiErrorKey } from '@/lib/passwordRules';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { AuthCard, AUTH_BUTTON_CLASS, PasswordInput, PasswordStrengthMeter } from './auth/AuthCard';

function readTokenOnce(): string {
  const url = new URL(window.location.href);
  const token = url.searchParams.get('token') ?? '';
  if (token) {
    url.searchParams.delete('token');
    window.history.replaceState(null, '', url.pathname + url.search + url.hash);
  }
  return token;
}

const LINK_BUTTON = 'inline-flex h-12 w-full items-center justify-center rounded-full bg-white font-medium text-black hover:bg-white/90';

export default function ResetPassword() {
  const { t } = useTranslation(['admin', 'common']);
  useDocumentTitle(t('admin:passwordReset.resetTitle'));
  const [token] = useState(readTokenOnce);
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [linkInvalid, setLinkInvalid] = useState(false);

  useEffect(() => {
    if (!token) setLinkInvalid(true);
  }, [token]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const localError = newPasswordError(password, confirmation);
    if (localError) {
      setError(t(localError));
      return;
    }
    setError('');
    setLoading(true);
    try {
      await resetPassword(token, password);
      setDone(true);
    } catch (err) {
      const apiErr = err instanceof ApiErrorException ? err : null;
      if (apiErr?.code === 'INVALID_RESET_TOKEN') setLinkInvalid(true);
      setError(t(passwordApiErrorKey(apiErr?.code, apiErr?.status)));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthCard title={t('admin:passwordReset.resetTitle')}>
      {done ? (
        <div className="space-y-6">
          <p className="text-white/80 text-sm" role="status">{t('admin:passwordReset.done')}</p>
          <Link to="/Login" className={LINK_BUTTON}>{t('admin:passwordReset.goToLogin')}</Link>
        </div>
      ) : linkInvalid ? (
        <div className="space-y-6">
          <div role="alert" className="space-y-2">
            <p className="text-sm font-medium text-red-400">
              {t(token ? 'admin:passwordReset.errors.invalidLink' : 'admin:passwordReset.errors.missingToken')}
            </p>
            <p className="text-sm text-white/70">
              {t(token ? 'admin:passwordReset.invalidLinkHelp' : 'admin:passwordReset.missingTokenHelp')}
            </p>
          </div>
          <Link to="/forgot-password" className={LINK_BUTTON}>{t('admin:passwordReset.requestNew')}</Link>
        </div>
      ) : (
        <form onSubmit={submit} noValidate className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="reset-password" className="text-white/80">{t('admin:passwordReset.newPassword')}</Label>
            <PasswordInput id="reset-password" value={password} onChange={setPassword} autoComplete="new-password" describedBy="reset-password-strength" autoFocus />
            <PasswordStrengthMeter id="reset-password-strength" password={password} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="reset-confirmation" className="text-white/80">{t('admin:passwordReset.confirmPassword')}</Label>
            <PasswordInput id="reset-confirmation" value={confirmation} onChange={setConfirmation} autoComplete="new-password" />
          </div>
          {error && <p className="text-red-400 text-sm" role="alert">{error}</p>}
          <Button type="submit" disabled={loading} aria-busy={loading} className={AUTH_BUTTON_CLASS}>
            {/* REV-W6-F5: the label stays for screen readers while the spinner turns. */}
            {loading && <Loader2 className="w-5 h-5 animate-spin motion-reduce:animate-none" aria-hidden />}
            <span className={loading ? 'sr-only' : undefined}>{t('admin:passwordReset.save')}</span>
          </Button>
        </form>
      )}
    </AuthCard>
  );
}
